import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Empty,
  InputNumber,
  Layout,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tooltip,
  Typography
} from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis
} from "recharts";
import { formatNumber } from "../../../util/NumberFormatter";
import MobileStatBox from "../../common/MobileStatBox";
import useIsMobile from "../../../util/useIsMobile";

const tierOrder = ['Normal', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Meteorite', 'Mithril'];
const tierDisplayMap = {
  'Mithril': '미스릴+',
  'Meteorite': '메테오라이트+',
  'Diamond': '다이아몬드+',
  'Platinum': '플래티넘+',
  'Gold': '골드+',
  'Silver': '실버+',
  'Bronze': '브론즈+',
  'Iron': '아이언+',
  'Normal': '전체(일반포함)'
};
const tierDropdownOptions = tierOrder.slice().reverse().map(tier => {
  const imagePath = `/img/tier/${tier}.png`;
  return {
    value: tier,
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={imagePath}
          alt={tier}
          style={{
            height: '20px',
            width: 'auto',
            marginRight: '8px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <span>{tierDisplayMap[tier]}</span>
      </div>
    ),
  };
});

const itemGradeColors = {
  'Common': '#9d9d9d',
  'Uncommon': '#2ecc71',
  'Rare': '#3498db',
  'Epic': '#9b59b6',
  'Legend': '#e67e22',
  'Mythic': '#e74c3c',
};

const getItemGradeColor = (grade) => {
  return itemGradeColors[grade] || '#d9d9d9';
};

const renderItemCell = (itemCode, itemName, itemGrade, materialCode) => {
  const color = getItemGradeColor(itemGrade);
  return (
    <Tooltip title={itemName} color="#ffffff" overlayInnerStyle={{ color: '#000000' }}>
      <div style={{
        position: 'relative',
        width: '60px',
        height: '60px',
        border: `2px solid ${color}`,
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(to bottom, ${color}40 0%, ${color}10 50%, #2d2d44 100%)`,
      }}>
        <img
          src={`/img/item/${itemCode}.png`}
          alt={itemName}
          style={{ width: '55px', height: '55px', objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {materialCode && (itemGrade === 'Legend' || itemGrade === 'Mythic') && (
          <img
            src={`/img/item/${materialCode}.png`}
            alt=""
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '25px',
              height: '25px',
              borderRadius: '50%',
              backgroundColor: '#2d2d44',
              padding: '1px',
              border: '1px solid #555',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>
    </Tooltip>
  );
};

function ItemBuildMobileCard({ stat, totalGameCount }) {
  const hasGames = stat.gameCount > 0;
  const pickRate = totalGameCount > 0 ? (stat.gameCount / totalGameCount) * 100 : 0;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.topThree / stat.gameCount) * 100 : 0;
  const avgRank = hasGames ? (stat.gameRank / stat.gameCount) : null;
  const rpGain = hasGames ? (stat.mmrGain / stat.gameCount) : null;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {renderItemCell(stat.itemWeapon, stat.itemWeaponName, stat.itemWeaponGrade, stat.itemWeaponMaterial)}
        {renderItemCell(stat.itemChest, stat.itemChestName, stat.itemChestGrade, stat.itemChestMaterial)}
        {renderItemCell(stat.itemHead, stat.itemHeadName, stat.itemHeadGrade, stat.itemHeadMaterial)}
        {renderItemCell(stat.itemArm, stat.itemArmName, stat.itemArmGrade, stat.itemArmMaterial)}
        {renderItemCell(stat.itemLeg, stat.itemLegName, stat.itemLegGrade, stat.itemLegMaterial)}
      </div>
      <Row gutter={[4, 6]}>
        <Col span={8}><MobileStatBox label="픽률" value={`${pickRate.toFixed(2)}% (${stat.gameCount})`} /></Col>
        <Col span={8}><MobileStatBox label="승률" value={hasGames ? `${winRate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="Top3" value={hasGames ? `${top3Rate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="평균등수" value={avgRank === null ? '-' : avgRank.toFixed(2)} /></Col>
        <Col span={8}><MobileStatBox label="RP" value={rpGain === null ? '-' : rpGain.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

function RouteMobileCard({ stat, totalGameCount, onRouteClick }) {
  const hasGames = stat.gameCount > 0;
  const pickRate = totalGameCount > 0 ? (stat.gameCount / totalGameCount) * 100 : 0;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.topThree / stat.gameCount) * 100 : 0;
  const avgRank = hasGames ? (stat.gameRank / stat.gameCount) : null;
  const rpGain = hasGames ? (stat.mmrGain / stat.gameCount) : null;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <Button type="link" style={{ padding: 0, marginBottom: 8, height: 'auto', fontWeight: 500 }} onClick={onRouteClick}>
        루트 ID {stat.routeIdOfStart}
      </Button>
      <Row gutter={[4, 6]}>
        <Col span={8}><MobileStatBox label="픽률" value={`${pickRate.toFixed(2)}% (${stat.gameCount})`} /></Col>
        <Col span={8}><MobileStatBox label="승률" value={hasGames ? `${winRate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="Top3" value={hasGames ? `${top3Rate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="평균등수" value={avgRank === null ? '-' : avgRank.toFixed(2)} /></Col>
        <Col span={8}><MobileStatBox label="RP" value={rpGain === null ? '-' : rpGain.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

function TacticalMobileCard({ stat, totalGameCount }) {
  const hasGames = stat.gameCount > 0;
  const pickRate = totalGameCount > 0 ? (stat.gameCount / totalGameCount) * 100 : 0;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.topThree / stat.gameCount) * 100 : 0;
  const avgRank = hasGames ? (stat.gameRank / stat.gameCount) : null;
  const rpGain = hasGames ? (stat.mmrGain / stat.gameCount) : null;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>{stat.tacticalSkillGroupName}</Typography.Text>
      <Row gutter={[4, 6]}>
        <Col span={8}><MobileStatBox label="픽률" value={`${pickRate.toFixed(2)}% (${stat.gameCount})`} /></Col>
        <Col span={8}><MobileStatBox label="승률" value={hasGames ? `${winRate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="Top3" value={hasGames ? `${top3Rate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="평균등수" value={avgRank === null ? '-' : avgRank.toFixed(2)} /></Col>
        <Col span={8}><MobileStatBox label="RP" value={rpGain === null ? '-' : rpGain.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

function TraitSubMobileCard({ stat }) {
  const hasGames = stat.gameCount > 0;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.topThree / stat.gameCount) * 100 : 0;
  const avgRank = hasGames ? (stat.gameRank / stat.gameCount) : null;
  const rpGain = hasGames ? (stat.mmrGain / stat.gameCount) : null;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        {stat.subTraitNames?.join(', ') || '-'}
      </Typography.Text>
      <Row gutter={[4, 6]}>
        <Col span={8}><MobileStatBox label="픽 수" value={stat.gameCount} /></Col>
        <Col span={8}><MobileStatBox label="승률" value={hasGames ? `${winRate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="Top3" value={hasGames ? `${top3Rate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}><MobileStatBox label="평균등수" value={avgRank === null ? '-' : avgRank.toFixed(2)} /></Col>
        <Col span={8}><MobileStatBox label="RP" value={rpGain === null ? '-' : rpGain.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

// 이터널 리턴 낮/밤 주기 (게임 시작부터 각 구간의 지속시간, 초 단위).
// 8일차 낮까지만 제공되어 그 이후(약 25.8분~)는 음영 없이 둔다.
const DAY_NIGHT_CYCLE = [
  { phase: 'day', seconds: 140 },   // 1일 낮
  { phase: 'night', seconds: 110 }, // 1일 밤
  { phase: 'day', seconds: 130 },   // 2일 낮
  { phase: 'night', seconds: 120 }, // 2일 밤
  { phase: 'day', seconds: 130 },   // 3일 낮
  { phase: 'night', seconds: 110 }, // 3일 밤
  { phase: 'day', seconds: 100 },   // 4일 낮
  { phase: 'night', seconds: 110 }, // 4일 밤
  { phase: 'day', seconds: 80 },    // 5일 낮
  { phase: 'night', seconds: 80 },  // 5일 밤
  { phase: 'day', seconds: 70 },    // 6일 낮
  { phase: 'night', seconds: 50 },  // 6일 밤
  { phase: 'day', seconds: 120 },   // 7일 낮
  { phase: 'night', seconds: 50 },  // 7일 밤
  { phase: 'day', seconds: 150 },   // 8일 낮
];

const DAY_NIGHT_FILL = { day: '#FFF8E1', night: '#E8EAF6' };

// 낮/밤 주기를 분 단위 x축 세그먼트로 변환하고, 표시 범위 [1, maxBucket] 로 클램프한다.
// 데이터가 짧으면 뒤 구간은 제외되고, 길면 정의된 구간(8일차 낮)까지만 칠해진다.
const buildDayNightSegments = (maxBucket) => {
  if (!maxBucket || maxBucket <= 1) return [];
  const segments = [];
  let startSec = 0;
  for (const { phase, seconds } of DAY_NIGHT_CYCLE) {
    const x1 = Math.max(startSec / 60, 1);
    const x2 = Math.min((startSec + seconds) / 60, maxBucket);
    startSec += seconds;
    if (x1 < x2) segments.push({ phase, x1, x2 });
  }
  return segments;
};

// 일차(낮+밤)별로 묶어 숫자 라벨을 둘 위치를 만든다.
// DAY_NIGHT_CYCLE 은 [1일 낮, 1일 밤, 2일 낮, 2일 밤, ...] 순서라 2개씩 한 일차.
// 라벨 위치(labelX): 낮+밤이 다 보이면 낮/밤 경계(낮 끝), 낮만 보이면 낮의 중앙.
// 표시 범위 [1분, maxBucket] 로 클램프하고, 구간 span/labelX 를 분 단위로 반환한다.
const buildDayLabelSegments = (maxBucket) => {
  if (!maxBucket || maxBucket <= 1) return [];
  const minSec = 60; // x축은 1분부터 표시
  const maxSec = maxBucket * 60;
  const days = [];
  let startSec = 0;
  DAY_NIGHT_CYCLE.forEach(({ phase, seconds }, index) => {
    const dayIndex = Math.floor(index / 2);
    if (!days[dayIndex]) {
      days[dayIndex] = { day: dayIndex + 1, startSec, switchSec: startSec, endSec: startSec, hasNight: false };
    }
    const d = days[dayIndex];
    if (phase === 'day') {
      d.switchSec = startSec + seconds; // 낮 끝 = 낮/밤 경계
    } else {
      d.hasNight = true;
    }
    d.endSec = startSec + seconds;
    startSec += seconds;
  });
  const result = [];
  for (const d of days) {
    const x1Sec = Math.max(d.startSec, minSec);
    const x2Sec = Math.min(d.endSec, maxSec);
    if (x1Sec >= x2Sec) continue;
    let labelSec;
    if (d.hasNight && d.switchSec > x1Sec && d.switchSec < x2Sec) {
      labelSec = d.switchSec; // 낮/밤 경계
    } else {
      const visibleDayEnd = Math.min(d.switchSec, x2Sec); // 낮만 보이는 경우의 낮 끝
      labelSec = (x1Sec + Math.max(visibleDayEnd, x1Sec)) / 2; // 보이는 낮 구간 중앙
    }
    labelSec = Math.min(Math.max(labelSec, x1Sec), x2Sec);
    result.push({ day: d.day, x1: x1Sec / 60, x2: x2Sec / 60, labelX: labelSec / 60 });
  }
  return result;
};

// 일차 숫자를 배경에 음각(engraved)처럼 큼직하고 흐릿하게 그린다.
// 어두운 본체 위에 1px 아래로 밝은 복제를 깔아 표면에 눌린 듯한 입체감을 준다.
// (라벨 ReferenceArea 는 Line 보다 먼저 렌더되어 곡선 뒤 워터마크처럼 보인다.)
const renderEngravedDayLabel = (seg) => (props) => {
  const { viewBox } = props;
  if (!viewBox || viewBox.width <= 0) return null;
  const { x, y, width, height } = viewBox;
  // 구간 [seg.x1, seg.x2] 가 픽셀 [x, x+width] 에 선형 대응하므로 labelX 를 보간한다.
  const span = seg.x2 - seg.x1;
  const ratio = span > 0 ? (seg.labelX - seg.x1) / span : 0.5;
  const cx = x + width * ratio;
  const cy = y + height / 2;
  const common = {
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: 44,
    fontWeight: 800,
    style: { pointerEvents: 'none', userSelect: 'none' },
  };
  return (
    <g>
      <text x={cx} y={cy + 1.5} fill="#ffffff" fillOpacity={0.6} {...common}>{seg.day}</text>
      <text x={cx} y={cy} fill="#4a4a4a" fillOpacity={0.18} {...common}>{seg.day}</text>
    </g>
  );
};

// 누락된 time_bucket 을 null 로 채워 x축 갭(표본 부족 구간)을 시각적으로 표현한다.
const buildPowerCurveChartData = (points) => {
  if (!points || points.length === 0) return [];
  const byBucket = new Map(points.map((p) => [p.time_bucket, p]));
  const maxBucket = Math.max(...points.map((p) => p.time_bucket));
  const result = [];
  for (let t = 1; t <= maxBucket; t++) {
    const point = byBucket.get(t);
    result.push(
      point || {
        time_bucket: t,
        sample_count: null,
        raw_zscore: null,
        smoothed_zscore: null,
        diff_raw_zscore: null,
        diff_smoothed_zscore: null
      }
    );
  }
  return result;
};

const formatZScore = (value) =>
  value === null || value === undefined ? '-' : value.toFixed(2);

// 표시되는 smoothed_zscore 의 min/max 에 위아래 5% 여유를 둔 y축 domain 을 계산한다.
// 값이 없으면 Recharts 자동 스케일(['auto', 'auto'])로 위임한다.
const buildPowerCurveYDomain = (points) => {
  const values = (points || [])
    .map((p) => p.smoothed_zscore)
    .filter((v) => v !== null && v !== undefined);
  if (values.length === 0) return ['auto', 'auto'];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const padding = range > 0 ? range * 0.05 : (Math.abs(max) * 0.05 || 0.1);
  return [min - padding, max + padding];
};

const PowerCurveTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  if (point.sample_count === null || point.sample_count === undefined) return null;
  return (
    <Card size="small" bodyStyle={{ padding: 8 }}>
      <Typography.Text strong>{label}분</Typography.Text>
      <div style={{ fontSize: 12, marginTop: 4 }}>
        <div>표본 수: {formatNumber(point.sample_count)}</div>
        <div>원본 z: {formatZScore(point.raw_zscore)}</div>
        <div>평활 z: {formatZScore(point.smoothed_zscore)}</div>
        <div>원본 차분: {formatZScore(point.diff_raw_zscore)}</div>
        <div>평활 차분: {formatZScore(point.diff_smoothed_zscore)}</div>
      </div>
    </Card>
  );
};

function PowerCurveSection({ characterName, bestWeapon }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    if (!characterName || !bestWeapon) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    axios.get("/api/external/bser/power-curve", {
      params: { characterName, bestWeapon }
    })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => {
        if (cancelled) return;
        const message = err.response?.data?.message || 'Power Curve 데이터를 불러오지 못했습니다.';
        setError(message);
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [characterName, bestWeapon]);

  const points = data?.points || [];
  const hasData = data && data.window_end_date && points.length > 0;
  const chartData = buildPowerCurveChartData(points);
  const yDomain = buildPowerCurveYDomain(points);
  const maxBucket = points.length ? Math.max(...points.map((p) => p.time_bucket)) : 0;
  const dayNightSegments = buildDayNightSegments(maxBucket);
  const dayLabelSegments = buildDayLabelSegments(maxBucket);
  const patchLabel = data && data.version_major != null
    ? `v${data.version_major}.${data.version_minor}`
    : null;

  return (
    <div>
      <Space align="center" style={{ marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>Power Curve</Typography.Title>
        {patchLabel && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{patchLabel} 기준</Typography.Text>
        )}
        {hasData && data.window_end_date && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            ({data.window_end_date} 까지 14일 윈도우)
          </Typography.Text>
        )}
      </Space>
      <Card size="small">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : error ? (
          <Empty description={error} />
        ) : !hasData ? (
          <Empty description="아직 적재된 데이터가 없습니다 (해당 빌드는 적재 전이거나 표본이 부족할 수 있습니다)." />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                {dayNightSegments.map((seg, index) => (
                  <ReferenceArea
                    key={`day-night-${index}`}
                    x1={seg.x1}
                    x2={seg.x2}
                    fill={DAY_NIGHT_FILL[seg.phase]}
                    fillOpacity={0.5}
                    ifOverflow="hidden"
                  />
                ))}
                {dayLabelSegments.map((seg) => (
                  <ReferenceArea
                    key={`day-label-${seg.day}`}
                    x1={seg.x1}
                    x2={seg.x2}
                    fill="none"
                    fillOpacity={0}
                    ifOverflow="visible"
                    label={renderEngravedDayLabel(seg)}
                  />
                ))}
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="time_bucket"
                  type="number"
                  domain={[1, 'dataMax']}
                  allowDecimals={false}
                  tickFormatter={(v) => `${v}분`}
                  fontSize={11}
                />
                <YAxis
                  fontSize={11}
                  width={48}
                  domain={yDomain}
                  allowDataOverflow
                  tickFormatter={(v) => v.toFixed(2)}
                />
                <RechartTooltip content={<PowerCurveTooltip />} />
                <ReferenceLine y={0} stroke="#999" strokeDasharray="4 4" />
                <Line
                  type="monotone"
                  dataKey="smoothed_zscore"
                  name="평활 z-score"
                  stroke="#4A90D9"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>

            <Space style={{ marginTop: 12 }}>
              <Switch checked={showDiff} onChange={setShowDiff} size="small" />
              <Typography.Text style={{ fontSize: 12 }}>분 구간 차분 보기 (양수=강해짐, 음수=약해짐)</Typography.Text>
            </Space>

            {showDiff && (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="time_bucket"
                    type="number"
                    domain={[1, 'dataMax']}
                    allowDecimals={false}
                    tickFormatter={(v) => `${v}분`}
                    fontSize={11}
                  />
                  <YAxis fontSize={11} width={48} />
                  <RechartTooltip content={<PowerCurveTooltip />} />
                  <ReferenceLine y={0} stroke="#999" />
                  <Bar dataKey="diff_smoothed_zscore" name="평활 차분">
                    {chartData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={(entry.diff_smoothed_zscore || 0) >= 0 ? '#43A047' : '#E53935'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default function CharacterStats() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const characterName = searchParams.get('characterName') || '';
  const bestWeapon = searchParams.get('bestWeapon') || '';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedMinTier, setSelectedMinTier] = useState('Diamond');
  const [mainWeathers, setMainWeathers] = useState({ '흐림': true, '쾌청': true, '비': true, '모래바람': true });
  const [subWeathers, setSubWeathers] = useState({ '무풍': true, '안개': true, '강풍': true, '벼락': true });
  const [daysAgo, setDaysAgo] = useState(3);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const isMobile = useIsMobile();

  const handleMinTierChange = (value) => setSelectedMinTier(value);
  const handleMainWeatherChange = (mainWeather) => setMainWeathers(prev => ({ ...prev, [mainWeather]: !prev[mainWeather] }));
  const handleSubWeatherChange = (subWeather) => setSubWeathers(prev => ({ ...prev, [subWeather]: !prev[subWeather] }));

  const handleDaysChange = (newDays) => {
    const validatedDays = (typeof newDays === 'number' && newDays > 0) ? newDays : 3;
    setDaysAgo(validatedDays);
  };

  const handlePresetClick = (presetDays) => {
    handleDaysChange(presetDays);
    setPopoverVisible(false);
  };

  const popoverContent = (
    <Space direction="vertical" style={{ width: '250px' }}>
      <InputNumber
        min={1}
        max={31}
        value={daysAgo}
        onChange={(value) => {
          handleDaysChange(value === null ? 1 : value);
        }}
        addonAfter="일 전"
        style={{ width: '100%' }}
      />
      <Space wrap>
        <Button onClick={() => handlePresetClick(1)}>하루 전</Button>
        <Button onClick={() => handlePresetClick(3)}>3일 전</Button>
        <Button onClick={() => handlePresetClick(7)}>1주 전</Button>
        <Button onClick={() => handlePresetClick(14)}>2주 전</Button>
        <Button onClick={() => handlePresetClick(31)}>한 달 전</Button>
      </Space>
    </Space>
  );

  const weatherFilterContent = (
    <Space direction="vertical" size="middle">
      <div>
        <Typography.Text strong>주 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(mainWeathers).map(mainWeather => (
              <Checkbox
                key={mainWeather}
                checked={mainWeathers[mainWeather]}
                onChange={() => handleMainWeatherChange(mainWeather)}
              >
                {mainWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
      <div>
        <Typography.Text strong>보조 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(subWeathers).map(subWeather => (
              <Checkbox
                key={subWeather}
                checked={subWeathers[subWeather]}
                onChange={() => handleSubWeatherChange(subWeather)}
              >
                {subWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
    </Space>
  );

  useEffect(() => {
    if (!characterName || !bestWeapon) return;

    setLoading(true);
    const main = Object.keys(mainWeathers).filter(weatherKey => mainWeathers[weatherKey]).join(',');
    const sub = Object.keys(subWeathers).filter(weatherKey => subWeathers[weatherKey]).join(',');

    axios.get("/api/external/bser/character-detail", {
      params: {
        days: daysAgo,
        tier: selectedMinTier,
        mainWeathers: main,
        subWeathers: sub,
        characterName: characterName,
        bestWeapon: bestWeapon
      }
    })
      .then(r => r.data)
      .then(data => setData(data))
      .finally(() => setLoading(false));
  }, [daysAgo, selectedMinTier, mainWeathers, subWeathers, characterName, bestWeapon]);

  const itemTotalGameCount = data?.itemStats?.reduce((sum, s) => sum + s.gameCount, 0) || 0;
  const routeTotalGameCount = data?.routeStats?.reduce((sum, s) => sum + s.gameCount, 0) || 0;
  const tacticalTotalGameCount = data?.tacticalStats?.reduce((sum, s) => sum + s.gameCount, 0) || 0;

  const itemColumns = [
    {
      title: '무기',
      align: 'center',
      width: 80,
      render: (stat) => renderItemCell(stat.itemWeapon, stat.itemWeaponName, stat.itemWeaponGrade, stat.itemWeaponMaterial)
    },
    {
      title: '상의',
      align: 'center',
      width: 80,
      render: (stat) => renderItemCell(stat.itemChest, stat.itemChestName, stat.itemChestGrade, stat.itemChestMaterial)
    },
    {
      title: '머리',
      align: 'center',
      width: 80,
      render: (stat) => renderItemCell(stat.itemHead, stat.itemHeadName, stat.itemHeadGrade, stat.itemHeadMaterial)
    },
    {
      title: '팔',
      align: 'center',
      width: 80,
      render: (stat) => renderItemCell(stat.itemArm, stat.itemArmName, stat.itemArmGrade, stat.itemArmMaterial)
    },
    {
      title: '다리',
      align: 'center',
      width: 80,
      render: (stat) => renderItemCell(stat.itemLeg, stat.itemLegName, stat.itemLegGrade, stat.itemLegMaterial)
    },
    {
      title: '픽률',
      dataIndex: 'gameCount',
      align: 'center',
      width: 90,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => {
        const total = data?.itemStats?.reduce((sum, s) => sum + s.gameCount, 0) || 1;
        return `${((gameCount / total) * 100).toFixed(2)}% (${gameCount})`;
      }
    },
    {
      title: '승률',
      align: 'center',
      width: 75,
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.victory / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: 'Top 3',
      align: 'center',
      width: 75,
      sorter: (a, b) => (a.gameCount > 0 ? a.topThree / a.gameCount : 0) - (b.gameCount > 0 ? b.topThree / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.topThree / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: '평균 등수',
      align: 'center',
      width: 80,
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : 999) - (b.gameCount > 0 ? b.gameRank / b.gameCount : 999),
      render: (stat) => stat.gameCount > 0 ? (stat.gameRank / stat.gameCount).toFixed(2) : '-'
    },
    {
      title: 'RP 획득량',
      align: 'center',
      width: 80,
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : 0) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? (stat.mmrGain / stat.gameCount).toFixed(2) : '-'
    }
  ];

  const routeColumns = [
    {
      title: '루트 ID',
      dataIndex: 'routeIdOfStart',
      align: 'center',
      render: (routeId) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/bser/route-detail?routeId=${routeId}`)}>
          {routeId}
        </Button>
      )
    },
    {
      title: '픽률',
      dataIndex: 'gameCount',
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => {
        const total = data?.routeStats?.reduce((sum, s) => sum + s.gameCount, 0) || 1;
        return `${((gameCount / total) * 100).toFixed(2)}% (${gameCount})`;
      }
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.victory / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.topThree / a.gameCount : 0) - (b.gameCount > 0 ? b.topThree / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.topThree / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : 999) - (b.gameCount > 0 ? b.gameRank / b.gameCount : 999),
      render: (stat) => stat.gameCount > 0 ? (stat.gameRank / stat.gameCount).toFixed(2) : '-'
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : 0) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? (stat.mmrGain / stat.gameCount).toFixed(2) : '-'
    }
  ];

  const tacticalColumns = [
    {
      title: '전술 스킬',
      dataIndex: 'tacticalSkillGroupName',
      align: 'center'
    },
    {
      title: '픽률',
      dataIndex: 'gameCount',
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => {
        const total = data?.tacticalStats?.reduce((sum, s) => sum + s.gameCount, 0) || 1;
        return `${((gameCount / total) * 100).toFixed(2)}% (${gameCount})`;
      }
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.victory / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.topThree / a.gameCount : 0) - (b.gameCount > 0 ? b.topThree / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.topThree / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : 999) - (b.gameCount > 0 ? b.gameRank / b.gameCount : 999),
      render: (stat) => stat.gameCount > 0 ? (stat.gameRank / stat.gameCount).toFixed(2) : '-'
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : 0) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? (stat.mmrGain / stat.gameCount).toFixed(2) : '-'
    }
  ];

  const traitSubColumns = [
    {
      title: '서브 특성',
      dataIndex: 'subTraitNames',
      align: 'center',
      render: (subTraitNames) => subTraitNames?.join(', ') || '-'
    },
    {
      title: '픽 수',
      dataIndex: 'gameCount',
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.gameCount - b.gameCount
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.victory / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.topThree / a.gameCount : 0) - (b.gameCount > 0 ? b.topThree / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? `${((stat.topThree / stat.gameCount) * 100).toFixed(2)}%` : '-'
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : 999) - (b.gameCount > 0 ? b.gameRank / b.gameCount : 999),
      render: (stat) => stat.gameCount > 0 ? (stat.gameRank / stat.gameCount).toFixed(2) : '-'
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : 0) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : 0),
      render: (stat) => stat.gameCount > 0 ? (stat.mmrGain / stat.gameCount).toFixed(2) : '-'
    }
  ];

  const traitCollapseItems = data?.traitStats?.map((traitGroup, index) => ({
    key: index.toString(),
    label: `코어 특성: ${traitGroup.traitFirstCoreName} (픽 수: ${traitGroup.gameCount})`,
    children: isMobile ? (
      <div>
        {traitGroup.subCombinations.map((stat, idx) => (
          <TraitSubMobileCard key={`${traitGroup.traitFirstCore}-${idx}`} stat={stat} />
        ))}
      </div>
    ) : (
      <Table
        pagination={false}
        dataSource={traitGroup.subCombinations}
        columns={traitSubColumns}
        rowKey={(record, idx) => `${traitGroup.traitFirstCore}-${idx}`}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    )
  })) || [];

  if (!characterName || !bestWeapon) {
    return (
      <Layout style={{ background: '#ffffff' }}>
        <Layout.Content style={{ padding: '20px' }}>
          <Typography.Text>캐릭터 정보가 없습니다. Top Ranker 페이지에서 캐릭터를 선택해주세요.</Typography.Text>
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content style={{ padding: '20px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <Space align="center">
              <img
                src={`/img/character/${characterName}.png`}
                alt={characterName}
                style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#d6d6d6' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Typography.Title level={4} style={{ margin: 0 }}>
                {characterName} [{bestWeapon}]
              </Typography.Title>
              {data?.summary?.characterTier && (
                <img
                  src={`/img/character_tier/${data.summary.characterTier}.svg`}
                  alt={data.summary.characterTier}
                  style={{ width: '36px', height: '36px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </Space>
            <Space wrap>
              <Popover
                content={popoverContent}
                title={<Typography.Text strong>기간 선택</Typography.Text>}
                trigger="click"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
              >
                <Button style={{ minWidth: '110px', textAlign: 'center' }}>
                  {`${daysAgo}일 전`}
                </Button>
              </Popover>
              <Select
                value={selectedMinTier}
                style={{ width: 140 }}
                onChange={handleMinTierChange}
                options={tierDropdownOptions}
                listHeight={288}
              />
              <Popover content={weatherFilterContent} title={<Typography.Text strong>날씨 필터 설정</Typography.Text>} trigger="click" placement="bottom">
                <Button>날씨 필터</Button>
              </Popover>
            </Space>
          </div>

          {data?.summary && (
            <Card size="small" loading={loading}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-around' }}>
                <Statistic
                  title="픽 수"
                  value={data.summary.gameCount}
                  formatter={(value) => formatNumber(value)}
                />
                <Statistic
                  title="승률"
                  value={data.summary.gameCount > 0 ? (data.summary.victory / data.summary.gameCount * 100) : 0}
                  precision={2}
                  suffix={`% (${data.summary.victory})`}
                />
                <Statistic
                  title="Top 3"
                  value={data.summary.gameCount > 0 ? (data.summary.top3 / data.summary.gameCount * 100) : 0}
                  precision={2}
                  suffix={`% (${data.summary.top3})`}
                />
                <Statistic
                  title="평균 등수"
                  value={data.summary.gameCount > 0 ? (data.summary.gameRank / data.summary.gameCount) : 0}
                  precision={2}
                />
                <Statistic
                  title="RP 획득량"
                  value={data.summary.gameCount > 0 ? (data.summary.mmrGain / data.summary.gameCount) : 0}
                  precision={2}
                />
                <Statistic
                  title="딜량"
                  value={data.summary.gameCount > 0 ? Math.floor(data.summary.damageToPlayer / data.summary.gameCount) : 0}
                  formatter={(value) => formatNumber(value)}
                />
                <Statistic
                  title="DPS"
                  value={data.summary.totalTime > 0 ? (data.summary.damageToPlayer / data.summary.totalTime) : 0}
                  precision={2}
                />
                <Statistic
                  title="킬 수"
                  value={data.summary.gameCount > 0 ? (data.summary.playerKill / data.summary.gameCount) : 0}
                  precision={2}
                />
                <Statistic
                  title="TK"
                  value={data.summary.gameCount > 0 ? (data.summary.teamKill / data.summary.gameCount) : 0}
                  precision={2}
                />
              </div>
            </Card>
          )}

          <Divider />

          <div>
            <Typography.Title level={5}>아이템 빌드</Typography.Title>
            {isMobile ? (
              <div>
                {(data?.itemStats || []).map((stat, index) => (
                  <ItemBuildMobileCard key={`item-${index}`} stat={stat} totalGameCount={itemTotalGameCount} />
                ))}
              </div>
            ) : (
              <Table
                loading={loading}
                pagination={false}
                dataSource={data?.itemStats || []}
                columns={itemColumns}
                rowKey={(record, index) => `item-${index}`}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            )}
          </div>

          <div>
            <Typography.Title level={5}>루트</Typography.Title>
            {isMobile ? (
              <div>
                {(data?.routeStats || []).map((stat, index) => (
                  <RouteMobileCard
                    key={`route-${index}`}
                    stat={stat}
                    totalGameCount={routeTotalGameCount}
                    onRouteClick={() => navigate(`/bser/route-detail?routeId=${stat.routeIdOfStart}`)}
                  />
                ))}
              </div>
            ) : (
              <Table
                loading={loading}
                pagination={false}
                dataSource={data?.routeStats || []}
                columns={routeColumns}
                rowKey={(record, index) => `route-${index}`}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            )}
          </div>

          <div>
            <Typography.Title level={5}>전술 스킬</Typography.Title>
            {isMobile ? (
              <div>
                {(data?.tacticalStats || []).map((stat, index) => (
                  <TacticalMobileCard key={`tactical-${index}`} stat={stat} totalGameCount={tacticalTotalGameCount} />
                ))}
              </div>
            ) : (
              <Table
                loading={loading}
                pagination={false}
                dataSource={data?.tacticalStats || []}
                columns={tacticalColumns}
                rowKey={(record, index) => `tactical-${index}`}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            )}
          </div>

          <div>
            <Typography.Title level={5}>특성</Typography.Title>
            <Collapse items={traitCollapseItems} />
          </div>

          <Divider />

          <PowerCurveSection characterName={characterName} bestWeapon={bestWeapon} />
        </Space>
      </Layout.Content>
    </Layout>
  );
}
